import { Tooltip } from '@neo/primitives/tooltip'
import find from 'lodash/find'
import forEach from 'lodash/forEach'
import fromPairs from 'lodash/fromPairs'
import map from 'lodash/map'
import { Info } from 'lucide-react'
import { getAccountIdentifier } from 'v2/pages/settings/integrations/utils/account-identifier'
import { actionTitle } from 'v2/pages/settings/integrations/utils/action-title'
import { providerKey } from 'v2/pages/settings/integrations/utils/provider-key'
import type { RActionDetails, RExternalOrgApp } from 'v2/types/api/settings'
import dayjs from 'v2/utils/dayjs'
import { ActionConnectionLabel } from './action-connection-label'

/**
 * The single way to bind a saved action that carries `app_provider` but no
 * `org_app_uuid` to a live connection. Actions saved before the integrations-service
 * cutover still carry the old provider key ("google-mail"), so the match goes
 * through `providerKey` rather than raw equality.
 */
export const findActionConnectionForSavedProvider = (
  apps: RExternalOrgApp[] | undefined,
  savedProvider: string,
) => find(apps, (app) => providerKey(app.provider) === providerKey(savedProvider))

/** Connections as flat select options — the "pick a connection first" step. */
export const buildConnectionOptions = (
  apps: RExternalOrgApp[],
  accountLabels: Record<string, string>,
) =>
  map(apps, (app) => {
    const accountInfo = accountLabels[app.id]
    return {
      value: app.id,
      label: <ActionConnectionLabel app={app} accountInfo={accountInfo} />,
      // The label is a node, so the search text has to be carried separately.
      searchValue: `${app.name} ${accountInfo ?? ''}`.trim(),
      icon: app.logo_url ? (
        <img src={app.logo_url} alt={app.name} className="size-4 rounded" />
      ) : undefined,
    }
  })

export const ActionOptionLabel = ({ action }: { action: RActionDetails }) => (
  <span className="flex min-w-0 items-center gap-1.5">
    <span className="truncate">{actionTitle(action.action_name)}</span>
    {action.description && (
      <Tooltip content={action.description} side="right" delayDuration={200}>
        <Info
          className="size-3.5 shrink-0 text-tertiary"
          aria-label={action.description}
          data-testid="icon-action-description"
        />
      </Tooltip>
    )}
  </span>
)

export const buildActionOption = (action: RActionDetails, value: string) => ({
  label: <ActionOptionLabel action={action} />,
  value,
  searchValue: `${actionTitle(action.action_name)} ${action.action_name} ${action.description ?? ''}`,
  // The info icon already shows the description; a native title would stack a second tooltip.
  title: '',
})

/** The actions of one connection, keyed by the same encoded identifier the grouped list uses. */
export const buildActionOptionsForConnection = (
  app: RExternalOrgApp | undefined,
  encodeValue: (actionName: string, appId: string) => string,
) => {
  if (!app) return []
  return map(app.actions ?? [], (action) =>
    buildActionOption(action, encodeValue(action.action_name, app.id)),
  )
}

export const buildActionConnectionAccountLabels = (
  apps: RExternalOrgApp[],
  userMap: Record<string, string>,
) => {
  const baseLabels = new Map(
    map(apps, (app): [string, string] => {
      const enrichedApp =
        app.integrated_by?.id && userMap[app.integrated_by.id]
          ? { ...app, integrated_by: { ...app.integrated_by, name: userMap[app.integrated_by.id] } }
          : app
      return [app.id, getAccountIdentifier(enrichedApp)]
    }),
  )
  const counts = new Map<string, number>()
  forEach(apps, (app) => {
    const key = `${app.name}|${baseLabels.get(app.id)}`
    counts.set(key, (counts.get(key) ?? 0) + 1)
  })

  return fromPairs(
    map(apps, (app) => {
      let label = baseLabels.get(app.id) ?? ''
      if ((!label || (counts.get(`${app.name}|${label}`) ?? 0) > 1) && app.created_on) {
        const created = dayjs(app.created_on).format('MMM D, hh:mm A')
        label = label ? `${label} · ${created}` : created
      }
      return [app.id, label]
    }),
  )
}
